import { UnitSystemEnum, useGetUserPreferencesForUnitSelectionQuery } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  styled,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
  },
  '& .MuiDialog-paper': {
    width: '47.2rem',
    borderRadius: '0.8rem',
    backgroundColor: '#FFF',
    marginTop: '18vh',
  },
});

const titleTextStyle = {
  fontFamily: 'Roboto',
  fontSize: '2rem',
  fontWeight: 500,
  lineHeight: '2.4rem',
  color: abyssColors.primary[500],
};

const bodyTextStyle = {
  fontFamily: 'Roboto',
  fontSize: '1.6rem',
  fontWeight: 400,
  lineHeight: '2rem',
  color: abyssColors.primary[500],
};

const labelTextStyle = {
  fontFamily: 'Roboto',
  fontSize: '1.2rem',
  fontWeight: 400,
  color: abyssColors.primary[300],
};

const buttonLabelStyle = {
  fontFamily: 'Roboto',
  fontSize: '1.4rem',
  fontWeight: 600,
  lineHeight: '1.8rem',
  textTransform: 'none' as const,
};

const cancelButtonStyle = {
  ...buttonLabelStyle,
  padding: '0.8rem 1.2rem',
  borderRadius: '0.4rem',
  border: `0.1rem solid ${abyssColors.primary[500]}`,
  color: abyssColors.primary[500],
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: abyssColors.primary[50],
    border: `0.1rem solid ${abyssColors.primary[500]}`,
  },
};

const confirmButtonStyle = {
  ...buttonLabelStyle,
  padding: '0.8rem 1.2rem',
  borderRadius: '0.4rem',
  backgroundColor: abyssColors.primary[500],
  color: abyssColors.primary[50],
  '&:hover': {
    backgroundColor: abyssColors.primary[600],
  },
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (unit: UnitSystemEnum) => void;
};

export const ConfirmUnitSystemDialog = ({ isOpen, onClose, onConfirm }: Props) => {
  const unitSystem = useAtomValue(state.unitSystem);
  const [pendingUnit, setPendingUnit] = useState<UnitSystemEnum>(unitSystem);

  const { data } = useGetUserPreferencesForUnitSelectionQuery({
    fetchPolicy: 'cache-first',
    skip: !isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    const serverUnit = data?.getUserPreferences?.unit;
    setPendingUnit(serverUnit ?? unitSystem);
  }, [isOpen, data?.getUserPreferences?.unit, unitSystem]);

  const handleSelectChange = (event: SelectChangeEvent<UnitSystemEnum>) => {
    setPendingUnit(event.target.value as UnitSystemEnum);
  };

  const handleConfirm = () => {
    onConfirm(pendingUnit);
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="confirm-unit-system-dialog-title"
    >
      <DialogTitle
        id="confirm-unit-system-dialog-title"
        sx={{ m: 0, padding: '2.4rem 2.4rem 0 2.4rem', ...titleTextStyle }}
      >
        Confirm Unit System
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '1.6rem',
            top: '1.6rem',
            color: abyssColors.primary[500],
            padding: 0,
          }}
        >
          <CloseIcon sx={{ fontSize: '2.4rem' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '1.6rem 2.4rem 0 2.4rem !important' }}>
        <Typography sx={bodyTextStyle}>
          You are about to export this report. Please select your preferred unit system before
          proceeding.
        </Typography>
        <Box sx={{ marginTop: '1.6rem' }}>
          <Typography sx={labelTextStyle}>Unit system</Typography>
          <Select<UnitSystemEnum>
            fullWidth
            value={pendingUnit}
            onChange={handleSelectChange}
            variant="standard"
            disableUnderline
            inputProps={{ 'data-testid': 'confirm-unit-system-select' }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <Box
                  component="img"
                  src="/assets/adornment-start.svg"
                  alt=""
                  sx={{ width: '2rem', height: '2rem' }}
                />
                <Typography sx={bodyTextStyle}>
                  {value === UnitSystemEnum.Metric ? 'Metric' : 'Imperial'}
                </Typography>
              </Box>
            )}
            sx={{
              marginTop: '0.4rem',
              '& .MuiSelect-select': {
                padding: '0.4rem 0',
              },
              '& .MuiSelect-icon': {
                color: abyssColors.primary[500],
              },
            }}
          >
            <MenuItem value={UnitSystemEnum.Metric} sx={bodyTextStyle}>
              Metric
            </MenuItem>
            <MenuItem value={UnitSystemEnum.Imperial} sx={bodyTextStyle}>
              Imperial
            </MenuItem>
          </Select>
        </Box>
        <Divider sx={{ marginTop: '0.8rem', marginBottom: '1.6rem' }} />
        <Typography sx={bodyTextStyle}>
          Please confirm that this is correct before proceeding.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '2.4rem', gap: '1.2rem' }}>
        <Button
          onClick={onClose}
          sx={cancelButtonStyle}
          data-testid="confirm-unit-system-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          sx={confirmButtonStyle}
          autoFocus
          data-testid="confirm-unit-system-confirm"
        >
          Confirm and Export
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
